import { assign, assertEvent, raise, sendParent, setup } from 'xstate';
import type { ScheduleEvent } from './events';
import { publishScheduleActor, viewAvailableSchedulesActor, type AvailableSchedule } from './schedule.actors';

export interface ScheduleContext {
  schedules: AvailableSchedule[];
  error?: string;
  message?: string;
}

const genericReadError = 'Available schedules could not be loaded.';

const errorText = (error: unknown, fallback: string) =>
  error instanceof Error && error.message ? error.message : fallback;

export const scheduleMachine = setup({
  types: {
    context: {} as ScheduleContext,
    events: {} as ScheduleEvent,
  },
  actors: {
    viewAvailableSchedules: viewAvailableSchedulesActor,
    publishSchedule: publishScheduleActor,
  },
}).createMachine({
  id: 'schedule',
  type: 'parallel',
  context: {
    schedules: [],
  },
  states: {
    available: {
      initial: 'idle',
      on: {
        'schedule.load-available': {
          target: '.loading',
          actions: assign({ error: () => undefined }),
        },
      },
      states: {
        idle: {},
        loading: {
          invoke: {
            id: 'viewAvailableSchedules',
            src: 'viewAvailableSchedules',
            onDone: {
              target: 'ready',
              actions: assign({
                schedules: ({ event }) => event.output,
                error: () => undefined,
              }),
            },
            onError: {
              target: 'error',
              actions: assign({ error: ({ event }) => errorText(event.error, genericReadError) }),
            },
          },
        },
        ready: {
          on: { 'schedule.changed': 'loading' },
        },
        error: {
          on: { 'schedule.changed': 'loading' },
        },
      },
    },
    write: {
      initial: 'idle',
      on: {
        'schedule.publish': {
          target: '.saving',
          actions: assign({ message: () => undefined }),
        },
      },
      states: {
        idle: {},
        saving: {
          invoke: {
            id: 'publishSchedule',
            src: 'publishSchedule',
            input: ({ event }) => {
              assertEvent(event, 'schedule.publish');
              return event.input;
            },
            onDone: {
              target: 'idle',
              actions: [
                assign({ message: () => undefined }),
                raise({ type: 'schedule.changed' }),
                sendParent({ type: 'schedule.published' }),
                sendParent({
                  type: 'ui.toast.push',
                  toast: { title: 'Schedule published.', tone: 'success' },
                }),
              ],
            },
            onError: {
              target: 'idle',
              actions: [
                assign({ message: ({ event }) => errorText(event.error, 'Schedule could not be published.') }),
                sendParent({
                  type: 'ui.toast.push',
                  toast: { title: 'Schedule could not be published.', tone: 'error' },
                }),
              ],
            },
          },
        },
      },
    },
  },
});
